import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Storage } from '../../Utils/StorageUtils';
import theme from '../ThemeOld/dark';
import { persistor } from '../../Config/Store';
import { SystemRoutes } from '../../Utils/RouteUtils';


interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    resetKeys?: Array<string | number>;
    showDetails?: boolean;
    navigate?: any;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.setState({
            error,
            errorInfo
        });

        // Log error to console in development
        if (process.env.NODE_ENV === 'development') {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }

        // Call custom error handler if provided
        this.props.onError?.(error, errorInfo);
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        // Reset error boundary when resetKeys change
        if (this.state.hasError && this.props.resetKeys) {
            
            const prevKeys = prevProps.resetKeys;
            const currentKeys = this.props.resetKeys;

            if (prevKeys && currentKeys) {
                const hasChanged = prevKeys.some((key, index) => key !== currentKeys[index]);
                if (hasChanged) {
                    this.resetError();
                }
            }
        }
    }
    
   clearStorageAndCookies = async () => {
    try {
        console.log('Clearing all site data...');
        try { persistor?.pause?.(); } catch(e) { console.warn('persistor.pause falhou', e); }

        try { await persistor?.purge?.(); } catch(e) { console.warn('persistor.purge falhou', e); }

        Storage.clearPersistKey?.('persist:root');

        try { persistor?.persist?.(); } catch(e) { console.warn('persistor.persist falhou', e); }

        window.location.replace(SystemRoutes.DASHBOARD_OVERVIEW);
    } catch (error) {
        console.error('Error clearing site data:', error);
        try {
            Storage.clearPersistKey?.('persist:root');
        } finally {
            window.location.reload();
        }
    }
};

    resetError = async () => {

        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
        await this.clearStorageAndCookies();

    };

    getErrorMessage(error: Error): string {
        // Handle specific error types
        if (error instanceof TypeError) {
            return 'Detectamos um problema com os dados armazenados no seu navegador.';
        }
        if (error.message.includes('Network Error') || error.message.includes('timeout')) {
            return 'Erro de conexão. Por favor, verifique sua internet.';
        }

        if (error.message.includes('401') || error.message.includes('403')) {
            return 'Sessão expirada. Por favor, faça login novamente.';
        }

        return 'Ocorreu um erro inesperado. Notifique a equipe de suporte.';
    }

    render() {
        if (this.state.hasError && this.state.error) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            const errorMessage = this.getErrorMessage(this.state.error);

            return (
                <Box id="back-to-top-anchor"
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  backgroundColor: theme.palette.background.default,
                                  minHeight: '100vh',
                                  paddingTop: 10,
                                  backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6))',
                                }}
                      >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            maxWidth: 600,
                            textAlign: 'center',
                            borderRadius: 2,
                            margin: 24
                        }}
                    >
                        <ErrorOutlineIcon
                            color="error"
                            sx={{ fontSize: 60, mb: 2 }}
                        />
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                            Atualize os dados do seu navegador
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {errorMessage}
                        </Typography>
                        {errorMessage.includes('dados armazenados no seu navegador') && (
                            <Typography variant="body1" color="text.secondary">
                                Sincronize os dados clicando no botão abaixo e tente outra vez.
                        </Typography>
                        )}

                        {this.props.showDetails && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1,
                                    textAlign: 'left',
                                    maxHeight: 200,
                                    overflow: 'auto'
                                }}
                            >
                                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </Typography>
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={this.resetError}
                            sx={{ mt: "24px" }}
                        >
                            Atualizar dados
                        </Button>
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;